import Twemoji from "react-twemoji";
import { useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  TransformComponent,
  TransformWrapper,
  useTransformContext,
  useTransformEffect,
  useTransformInit,
} from "react-zoom-pan-pinch";
import { PuzzleType } from "sockapi/puzzle-data";
import RoundView, {
  DesertIslandRoundView,
} from "components/Movables/RoundView";
import { DESERT_ISLAND_ROUND_ID } from "components/PuzzleContent/IslandFinder";
import { PuzzleLink } from "components/Link";
import { LargeSpinner } from "components/Loading";
import { Paper, Metal } from "components/Material";
import Tooltip from "components/Tooltip";
import { useRound, useSlug, useInRound, useNavStore } from "stores/NavStore";
import { useTeamScopeState, useAccessibleRounds } from "stores/ScopeStateStore";
import { useSockClient } from "stores/ServerInteractionStore";
import { useEvent } from "client-util/useEvent";
import { addFlushSockAPIMessagesEffect } from "client-util/util";
import Globals from "Globals";
import "./RoundPage.css";

/** Currently portaled to WorldBase toolbar... */
function RoundMiniMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const clickingRef = useRef<boolean>(false);
  const ctx = useTransformContext();

  const renderMiniMap = useEvent(() => {
    const wrapper = ctx.wrapperComponent?.getBoundingClientRect();
    const content = ctx.contentComponent?.getBoundingClientRect();

    const map = mapRef.current?.getBoundingClientRect();
    const preview = previewRef.current;

    if (!wrapper || !content || !map || !preview) return;

    const scale = Math.min(
      map.width / content.width,
      map.height / content.height
    );

    preview.style.transform = ctx.handleTransformStyles(
      -ctx.transformState.positionX * scale,
      -ctx.transformState.positionY * scale,
      1
    );
    preview.style.width = `${wrapper.width * scale}px`;
    preview.style.height = `${wrapper.height * scale}px`;
  });

  useTransformEffect(renderMiniMap);
  useTransformInit(renderMiniMap);

  const moveMiniMap = useEvent((evt: MouseEvent) => {
    if (!clickingRef.current) return;
    evt.preventDefault();
    const wrapper = ctx.wrapperComponent?.getBoundingClientRect();
    const content = ctx.contentComponent?.getBoundingClientRect();
    const map = mapRef.current?.getBoundingClientRect();
    const preview = previewRef.current?.getBoundingClientRect();
    if (!wrapper || !content || !map || !preview) return;

    const scale = Math.min(
      map.width / content.width,
      map.height / content.height
    );
    const clamp = (val: number, max: number): number =>
      max > 0 ? Math.min(max, Math.max(0, val)) : max / 2;
    ctx.setTransformState(
      ctx.transformState.scale,
      -clamp(
        evt.clientX - preview.width / 2 - map.left,
        map.width - preview.width
      ) / scale,
      -clamp(
        evt.clientY - preview.height / 2 - map.top,
        map.height - preview.height
      ) / scale
    );
  });

  useEffect(() => {
    const stopMiniMap = () => {
      mapRef.current?.classList.remove("dragging");
      clickingRef.current = false;
    };
    document.addEventListener("mousemove", moveMiniMap);
    document.addEventListener("mouseup", stopMiniMap);
    return () => {
      document.removeEventListener("mousemove", moveMiniMap);
      document.removeEventListener("mouseup", stopMiniMap);
    };
  }, [moveMiniMap]);

  const round = useRound();
  const outlet = document.getElementById("world-base-round-minimap-container");
  if (!round || !outlet) return null;

  return createPortal(
    <>
      <Tooltip text="Scroll to zoom. Shift-drag, or drag the background, to pan.">
        <Metal rotate={0} seed="RoundPage: minimap label">
          <Twemoji>❓</Twemoji>
        </Metal>
      </Tooltip>
      <Paper seed={round.displayName}>
        <div
          className="round-mini-map"
          ref={mapRef}
          onMouseDown={(evt) => {
            clickingRef.current = true;
            mapRef.current?.classList.add("dragging");
            moveMiniMap(evt.nativeEvent);
          }}
          onWheel={(evt) => ctx.onWheelZoom(evt.nativeEvent)}
        >
          <div className="round-mini-map-preview" ref={previewRef} />
        </div>
      </Paper>
    </>,
    outlet
  );
}

function RoundPuzzleList() {
  const round = useRound();
  const teamScopeState = useTeamScopeState();

  if (teamScopeState === null || round === null)
    return <LargeSpinner inline={true} />;

  const puzzlesForRound = Object.values(teamScopeState.puzzles)
    .filter(
      (puzData) =>
        puzData.type === PuzzleType.PUZZLE && puzData.roundId === round.roundId
    )
    .sort((puzData1, puzData2) => puzData1.unlockTime - puzData2.unlockTime);
  return (
    <>
      {puzzlesForRound.map((puzData) => (
        <PuzzleLink
          key={puzData.puzName}
          puzName={puzData.puzName}
          type="puzzle"
          button
          solved={puzData.solveTime !== undefined}
        />
      ))}
    </>
  );
}

function RoundPage() {
  const round = useRound();
  const rounds = useAccessibleRounds();
  const slug = useSlug();
  const inRound = useInRound();
  const sockClient = useSockClient();
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);

  const needRedirectForInvalidRound = (() => {
    // Handle effects going out of sync with React Router.
    if (!inRound) return null;
    if (rounds === null) return null;
    return !Object.values(rounds).some((round) => round.slug === slug);
  })();
  useEffect(() => {
    if (!(needRedirectForInvalidRound ?? false)) return;
    return addFlushSockAPIMessagesEffect(sockClient, () => {
      toastr.error("Invalid round name.");
      setRedirectRoute({ route: "puzzles", replace: true });
    });
  }, [needRedirectForInvalidRound, setRedirectRoute, sockClient]);

  if (needRedirectForInvalidRound === null || round === null)
    return <LargeSpinner inline={true} />;

  const roundClassName = "round-" + round.slug.replaceAll("_", "-");
  if (round.roundId === DESERT_ISLAND_ROUND_ID) {
    return (
      <>
        <title>{round.displayName}</title>
        <div id="desert-round-base">
          <div id="round-wrapper" className={roundClassName}>
            <DesertIslandRoundView />
          </div>
        </div>
        <div className="round-puzzle-list">
          <RoundPuzzleList />
        </div>
      </>
    );
  }

  return (
    <>
      <title>{round.displayName}</title>
      <div id="round-base" />
      <TransformWrapper
        disablePadding={true}
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: true }}
      >
        <TransformComponent
          wrapperStyle={{ height: "100%", width: "100%" }}
          contentStyle={{ width: "100%" }}
        >
          <div
            id="round-wrapper"
            className={roundClassName}
            onMouseDown={(e) => {
              // Prevent panning if: shift key isn't held, and click happened
              // on a movable.
              if (
                !e.shiftKey &&
                [
                  "box-view",
                  "box-content",
                  "box-body",
                  "box-drag-target",
                  "piece-view",
                  "piece-content",
                ].some((c) => (e.target as HTMLElement).matches(`.${c} *`))
              ) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <RoundView />
          </div>
        </TransformComponent>
        <RoundMiniMap />
      </TransformWrapper>
      <div className="ambient-box-clipboard">
        <Tooltip
          text={
            ["pans_laboratory", "the_atoll", "abandoned_site"].includes(
              round.slug
            )
              ? "Show icons and highlight all pieces not in any puzzles"
              : "Highlight all pieces not in any puzzles"
          }
        >
          <button
            id="world-base-highlight-button"
            onClick={(e) => {
              const button = e.currentTarget;
              if (button.classList.contains("focused")) {
                button.classList.remove("focused");
              } else {
                button.classList.add("focused");
              }
            }}
          >
            <Twemoji>🔦️</Twemoji>
          </button>
        </Tooltip>
        <Tooltip text="Copy all pieces not in any puzzles">
          <button
            className="clipboard-button"
            onClick={(e) => {
              Globals.handleClipboardButton(
                document.body.querySelector(".ambient-box") as HTMLElement,
                e.currentTarget
              );
            }}
          >
            <Twemoji>📋️</Twemoji>
          </button>
        </Tooltip>
      </div>
    </>
  );
}

export default RoundPage;
